import type {
  CriteriaCriterion,
  CriteriaQueryEdit,
  CriteriaRule,
  PrescreeningVersion,
} from '../api/types';

// Criteria queries as an operator reads and edits them: each rule in plain
// words, the editable part of a version's document, and the checks Cortex can
// run before Rudi checks the document against the protocol schema.

export const SEX_TEXT: Record<string, string> = {
  F: 'female',
  M: 'male',
  D: 'diverse',
  X: 'not stated',
};

export const CERTAINTY_TEXT: Record<string, string> = {
  G: 'confirmed',
  V: 'suspected',
  A: 'ruled out',
  Z: 'history of',
};

const OPERATOR_TEXT: Record<string, string> = {
  '<': 'below',
  '<=': 'at most',
  '>': 'above',
  '>=': 'at least',
  '=': 'equal to',
  '!=': 'not equal to',
};

export const STAGE_TEXT: Record<number, string> = {
  1: 'Stage 1, condition',
  2: 'Stage 2, coded data',
  3: 'Stage 3, patient model',
  4: 'Stage 4, result',
};

export function stageText(stage: number | null | undefined): string {
  return stage ? (STAGE_TEXT[stage] ?? `Stage ${stage}`) : 'No stage';
}

function within(months: number | undefined): string | null {
  if (!months) return null;
  return months === 1 ? 'in the last month' : `in the last ${months} months`;
}

function parts(...values: Array<string | number | null | undefined | false>): string {
  return values.filter(Boolean).join(', ');
}

/**
 * A rule in plain words. With the version's code sets, an observation also
 * names the lab codes it reads, because a wrong code there is the usual cause
 * of many unknowns.
 */
export function describeRule(
  rule: CriteriaRule | null | undefined,
  codeSets?: Record<string, string[]> | null
): string {
  if (!rule) return 'No coded rule';
  if ('age' in rule) {
    const { min, max } = rule.age;
    if (min != null && max != null) return `Age ${min} to ${max}`;
    if (min != null) return `Age ${min} or older`;
    if (max != null) return `Age ${max} or younger`;
    return 'Any age';
  }
  if ('sex' in rule) {
    return `Sex ${rule.sex.in.map(value => SEX_TEXT[value] ?? value).join(' or ')}`;
  }
  if ('diagnosis' in rule) {
    const { icd10, match, certainty, withinMonths, longTermOnly } = rule.diagnosis;
    return parts(
      `Diagnosis ${icd10.join(', ')}${match === 'prefix' ? ' or a subcode' : ''}`,
      certainty?.length &&
        `certainty ${certainty.map(value => CERTAINTY_TEXT[value] ?? value).join(' or ')}`,
      longTermOnly && 'long-term diagnoses only',
      within(withinMonths)
    );
  }
  if ('medication' in rule) {
    const { atc, pzn, withinMonths } = rule.medication;
    return parts(
      atc?.length && `Medication with ATC ${atc.join(', ')}`,
      pzn?.length && `Medication with PZN ${pzn.join(', ')}`,
      within(withinMonths)
    );
  }
  if ('observation' in rule) {
    const { codeSet, op, value, unit, withinMonths, use } = rule.observation;
    const codes = codeSets?.[codeSet];
    return parts(
      `${codeSet} ${OPERATOR_TEXT[op] ?? op} ${unit ? `${value} ${unit}` : value}`,
      use === 'latest' ? 'latest value' : 'any value',
      within(withinMonths),
      codes
        ? codes.length
          ? `codes ${codes.join(', ')}`
          : 'no codes in its code set'
        : codeSets && 'its code set is missing'
    );
  }
  if ('service' in rule) {
    const { codes, system, withinMonths } = rule.service;
    return parts(
      `Service ${codes.join(', ')}${system ? ` (${system})` : ''}`,
      within(withinMonths)
    );
  }
  if ('lastEncounter' in rule) {
    const months = rule.lastEncounter.withinMonths;
    return months === 1 ? 'Seen in the last month' : `Seen in the last ${months} months`;
  }
  if ('all' in rule)
    return `All of: ${rule.all.map(part => describeRule(part, codeSets)).join('; ')}`;
  if ('any' in rule)
    return `Any of: ${rule.any.map(part => describeRule(part, codeSets)).join('; ')}`;
  if ('not' in rule) return `Not: ${describeRule(rule.not, codeSets)}`;
  return `A rule Cortex cannot show: ${Object.keys(rule).join(', ')}`;
}

/** The part of a version an operator edits, as the editor shows it. */
export function editableDocument(version: PrescreeningVersion): CriteriaQueryEdit {
  const document = version.document;
  if (document && Array.isArray(document.criteria)) {
    return {
      codeSets: (document.codeSets as Record<string, string[]> | undefined) ?? {},
      candidateThreshold:
        typeof document.candidateThreshold === 'number' ? document.candidateThreshold : 0.7,
      criteria: document.criteria as CriteriaCriterion[],
    };
  }
  // An older Rudi without the document: rebuild it from the criteria it lists.
  return {
    codeSets: version.codeSets ?? {},
    candidateThreshold: version.candidateThreshold ?? 0.7,
    criteria: version.criteria.map(criterion => {
      const rebuilt: CriteriaCriterion = {
        id: criterion.id,
        kind: criterion.kind,
        text: criterion.text,
        stage: (criterion.stage ?? 2) as CriteriaCriterion['stage'],
        rule: criterion.rule ?? null,
      };
      if (criterion.question) rebuilt.question = criterion.question;
      if (criterion.keywords?.length) rebuilt.keywords = criterion.keywords;
      if (criterion.textKinds?.length) rebuilt.textKinds = criterion.textKinds;
      return rebuilt;
    }),
  };
}

export const CODE_SET_NAME = /^[a-z][a-z0-9_]{0,63}$/;
const CRITERION_ID = /^(in|ex)-[0-9]{1,3}[a-z]?$/;
const RULE_KINDS = new Set([
  'age',
  'sex',
  'diagnosis',
  'medication',
  'observation',
  'service',
  'lastEncounter',
  'all',
  'any',
  'not',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringList(value: unknown, min = 1): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= min &&
    value.every(item => typeof item === 'string' && item.trim() !== '')
  );
}

function isMonths(value: unknown): boolean {
  return value === undefined || (Number.isInteger(value) && (value as number) >= 1);
}

function ruleErrors(rule: unknown, path: string, codeSets: Set<string>, errors: string[]): void {
  if (!isObject(rule)) {
    errors.push(`${path}: a rule is an object or null`);
    return;
  }
  const kinds = Object.keys(rule);
  if (kinds.length !== 1) {
    errors.push(`${path}: a rule holds exactly one kind`);
    return;
  }
  const kind = kinds[0];
  const body = rule[kind];
  const here = `${path}/${kind}`;
  if (!RULE_KINDS.has(kind)) {
    errors.push(`${here}: not a rule kind`);
    return;
  }
  if (kind === 'all' || kind === 'any') {
    if (!Array.isArray(body) || body.length === 0) errors.push(`${here}: needs a list of rules`);
    else body.forEach((part, index) => ruleErrors(part, `${here}/${index}`, codeSets, errors));
    return;
  }
  if (kind === 'not') {
    ruleErrors(body, here, codeSets, errors);
    return;
  }
  if (!isObject(body)) {
    errors.push(`${here}: needs an object`);
    return;
  }
  if (!isMonths(body.withinMonths)) errors.push(`${here}/withinMonths: a whole number from 1`);
  switch (kind) {
    case 'age': {
      const { min, max } = body;
      if (min === undefined && max === undefined) errors.push(`${here}: needs min or max`);
      for (const [name, value] of [
        ['min', min],
        ['max', max],
      ] as const)
        if (value !== undefined && !(Number.isInteger(value) && Number(value) >= 0))
          errors.push(`${here}/${name}: a whole number of years`);
      if (typeof min === 'number' && typeof max === 'number' && min > max)
        errors.push(`${here}: min is above max`);
      break;
    }
    case 'sex':
      if (!isStringList(body.in) || body.in.some(value => !(value in SEX_TEXT)))
        errors.push(`${here}/in: a list of F, M, D or X`);
      break;
    case 'diagnosis':
      if (!isStringList(body.icd10)) errors.push(`${here}/icd10: needs ICD-10-GM codes`);
      if (body.match !== 'prefix' && body.match !== 'exact')
        errors.push(`${here}/match: prefix or exact`);
      break;
    case 'medication':
      if (!isStringList(body.atc) && !isStringList(body.pzn))
        errors.push(`${here}: needs atc or pzn codes`);
      break;
    case 'observation':
      if (typeof body.codeSet !== 'string' || !codeSets.has(body.codeSet))
        errors.push(`${here}/codeSet: names no code set in codeSets`);
      if (typeof body.op !== 'string' || !(body.op in OPERATOR_TEXT))
        errors.push(`${here}/op: one of <, <=, >, >=, =, !=`);
      if (typeof body.value !== 'number') errors.push(`${here}/value: a number`);
      if (body.use !== 'latest' && body.use !== 'any') errors.push(`${here}/use: latest or any`);
      break;
    case 'service':
      if (!isStringList(body.codes)) errors.push(`${here}/codes: needs EBM or GOAE codes`);
      break;
    case 'lastEncounter':
      if (body.withinMonths === undefined) errors.push(`${here}/withinMonths: required`);
      break;
  }
}

/**
 * What is wrong with an edited document, as JSON pointers with a reason.
 * Empty when Cortex finds nothing; Rudi still checks the full schema.
 */
export function criteriaDocumentErrors(document: unknown): string[] {
  if (!isObject(document)) return ['The document is a JSON object'];
  const errors: string[] = [];
  const codeSets = document.codeSets;
  const names = new Set<string>();
  if (!isObject(codeSets)) {
    errors.push('/codeSets: an object of code set names to lists of codes');
  } else {
    for (const [name, codes] of Object.entries(codeSets)) {
      names.add(name);
      if (!CODE_SET_NAME.test(name))
        errors.push(`/codeSets/${name}: a name is lower case letters, digits and underscores`);
      if (!isStringList(codes, 0) || codes.length > 500 || codes.some(code => code.length > 64))
        errors.push(`/codeSets/${name}: up to 500 codes of 1 to 64 characters`);
    }
  }
  const threshold = document.candidateThreshold;
  if (
    threshold !== undefined &&
    !(typeof threshold === 'number' && threshold >= 0 && threshold <= 1)
  )
    errors.push('/candidateThreshold: a number from 0 to 1');
  const criteria = document.criteria;
  if (!Array.isArray(criteria) || criteria.length === 0) {
    errors.push('/criteria: needs at least one criterion');
    return errors;
  }
  if (criteria.length > 200) errors.push('/criteria: at most 200 criteria');
  const seen = new Set<string>();
  criteria.forEach((criterion, index) => {
    const path = `/criteria/${index}`;
    if (!isObject(criterion)) {
      errors.push(`${path}: a criterion is an object`);
      return;
    }
    const id = criterion.id;
    if (typeof id !== 'string' || !CRITERION_ID.test(id))
      errors.push(`${path}/id: like in-01 or ex-03a`);
    else if (seen.has(id)) errors.push(`${path}/id: ${id} appears twice`);
    else seen.add(id);
    if (criterion.kind !== 'inclusion' && criterion.kind !== 'exclusion')
      errors.push(`${path}/kind: inclusion or exclusion`);
    if (typeof criterion.text !== 'string' || !criterion.text.trim())
      errors.push(`${path}/text: required`);
    if (criterion.stage !== 1 && criterion.stage !== 2 && criterion.stage !== 3)
      errors.push(`${path}/stage: 1, 2 or 3`);
    if (!('rule' in criterion)) errors.push(`${path}/rule: required, null for no coded rule`);
    else if (criterion.rule !== null) ruleErrors(criterion.rule, `${path}/rule`, names, errors);
    if (
      criterion.question !== undefined &&
      criterion.question !== null &&
      typeof criterion.question !== 'string'
    )
      errors.push(`${path}/question: text or null`);
    if (criterion.keywords !== undefined && !isStringList(criterion.keywords, 0))
      errors.push(`${path}/keywords: a list of words`);
  });
  return errors;
}

/** The parsed document, or the reasons it cannot be sent. */
export function parseCriteriaDocument(
  text: string
): { document: CriteriaQueryEdit; errors: [] } | { document: null; errors: string[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return {
      document: null,
      errors: [`Not valid JSON: ${error instanceof Error ? error.message : String(error)}`],
    };
  }
  const errors = criteriaDocumentErrors(parsed);
  if (errors.length) return { document: null, errors };
  return { document: parsed as CriteriaQueryEdit, errors: [] };
}

/** Code sets from the editor's rows: trimmed codes, without repeats. */
export function parseCodes(text: string): string[] {
  return [
    ...new Set(
      text
        .split(',')
        .map(code => code.trim())
        .filter(Boolean)
    ),
  ];
}

export interface CodeSetRow {
  name: string;
  codes: string;
}

/** The rows' code sets, or the reasons Rudi would refuse them. */
export function codeSetRowErrors(rows: CodeSetRow[]): string[] {
  const errors: string[] = [];
  if (rows.length > 200) errors.push('At most 200 code sets.');
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    const name = row.name.trim();
    const label = name || `Code set ${index + 1}`;
    if (!CODE_SET_NAME.test(name))
      errors.push(
        `${label}: a name starts with a lower case letter and has only lower case letters, digits and underscores.`
      );
    else if (seen.has(name)) errors.push(`${label}: the name appears twice.`);
    seen.add(name);
    const codes = parseCodes(row.codes);
    if (codes.length === 0 || codes.length > 500) errors.push(`${label}: needs 1 to 500 codes.`);
    const long = codes.find(code => code.length > 64);
    if (long) errors.push(`${label}: ${long.slice(0, 20)}… is longer than 64 characters.`);
  });
  return errors;
}

export function codeSetsFromRows(rows: CodeSetRow[]): Record<string, string[]> {
  return Object.fromEntries(rows.map(row => [row.name.trim(), parseCodes(row.codes)]));
}

export function rowsFromCodeSets(codeSets: Record<string, string[]>): CodeSetRow[] {
  return Object.entries(codeSets).map(([name, codes]) => ({ name, codes: codes.join(', ') }));
}

/**
 * How a criterion differs from the version before: new, changed or the same.
 * Only the parts that change what the practice computer evaluates count.
 */
export function criterionChange(
  criterion: { id: string; rule?: unknown; stage?: unknown; kind?: unknown; question?: unknown },
  previous: PrescreeningVersion | undefined
): 'new' | 'changed' | null {
  if (!previous) return null;
  const before = previous.criteria.find(item => item.id === criterion.id);
  if (!before) return 'new';
  const shape = (item: typeof criterion) =>
    JSON.stringify([item.kind, item.stage ?? null, item.rule ?? null, item.question ?? null]);
  return shape(before) === shape(criterion) ? null : 'changed';
}
